import {Tooltip} from '@mui/material';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {createPortal} from 'react-dom';

import {useEntityProject} from '../../context';
import {useGetTraceServerClientContext} from '../wfReactInterface/traceServerClientContext';
import {
  projectIdFromParts,
  useRootObjectVersions,
} from '../wfReactInterface/tsDataModelHooks';
import {
  KnownBaseObjectClassType,
  ObjectVersionSchema,
} from '../wfReactInterface/wfDataModelHooksInterface';

const DEBOUNCE_DELAY_MS = 200;
const DROPDOWN_GAP_PX = 4;

const formatVersionTimestamp = (timestampMs: number): string => {
  return new Date(timestampMs).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const parseValue = (value: string | null): [string | null, string | null] => {
  if (!value) {
    return [null, null];
  }
  const parts = value.split(':');
  return parts.length === 2 ? [parts[0], parts[1]] : [value, null];
};

interface ObjectVersionSelectorProps {
  objectClass: KnownBaseObjectClassType;
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

const TOOLTIP_PROPS = {
  arrow: true,
  enterDelay: 500,
  enterNextDelay: 500,
  leaveDelay: 0,
  disableInteractive: true,
  PopperProps: {
    style: {
      zIndex: 2147483647,
    },
  },
  slotProps: {
    tooltip: {
      sx: {
        fontFamily: '"Source Sans Pro", sans-serif',
      },
    },
  },
};

/**
 * A reusable component for selecting object names and versions.
 *
 * Displays a single dropdown with a two-pane interface:
 * - Left pane: scrollable list of object names
 * - Right pane: versions for the hovered name
 *
 * Clicking a name selects "name + all versions".
 * Clicking a version selects "name + specific version".
 *
 * Entity and project are obtained from EntityProjectContext.
 *
 * @param objectClass - The object class to fetch (e.g., 'Dataset', 'Evaluation')
 * @param value - Current selection in format "name:hash" or "name"
 * @param onChange - Callback when selection changes
 * @param placeholder - Placeholder text for the selector
 * @param icon - Optional icon to display
 * @param disabled - Whether the selector is disabled
 */
export const ObjectVersionSelector: React.FC<ObjectVersionSelectorProps> = ({
  objectClass,
  value,
  onChange,
  placeholder = 'Select object',
  icon,
  disabled = false,
}) => {
  const {entity, project} = useEntityProject();
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredName, setHoveredName] = useState<string | null>(null);
  const [displayedName, setDisplayedName] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [versionCache, setVersionCache] = useState<
    Map<string, ObjectVersionSchema[]>
  >(new Map());
  const [loadingVersions, setLoadingVersions] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const latestHoverRef = useRef<string | null>(null);

  // Fetch only latest versions to get object names
  const objectsQuery = useRootObjectVersions({
    entity,
    project,
    filter: {baseObjectClasses: [objectClass], latestOnly: true},
    metadataOnly: true,
  });

  const names = useMemo(() => {
    const items = objectsQuery.result ?? [];
    return items.map(item => item.objectId).sort((a, b) => a.localeCompare(b));
  }, [objectsQuery.result]);

  const getTsClient = useGetTraceServerClientContext();

  const [selectedName, selectedHash] = useMemo(
    () => parseValue(value),
    [value]
  );

  const hoveredVersions = useMemo(() => {
    if (!displayedName) {
      return [];
    }
    return versionCache.get(displayedName) || [];
  }, [displayedName, versionCache]);

  const handleNameHover = useCallback((name: string | null) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    latestHoverRef.current = name;
    setHoveredName(name);

    if (name === null) {
      setDisplayedName(null);
      return;
    }

    hoverTimeoutRef.current = setTimeout(() => {
      if (latestHoverRef.current === name) {
        setDisplayedName(name);
      }
    }, DEBOUNCE_DELAY_MS);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Fetch versions for a specific object
  const fetchVersionsForObject = useCallback(
    async (objectId: string) => {
      if (versionCache.has(objectId)) {
        return; // Already loaded
      }

      setLoadingVersions(true);
      try {
        const client = getTsClient();
        const response = await client.objsQuery({
          project_id: projectIdFromParts({entity, project}),
          filter: {
            object_ids: [objectId],
            is_op: false,
          },
          metadata_only: true,
        });

        const versions = (response.objs || []).map(obj => ({
          entity,
          project,
          objectId: obj.object_id,
          versionHash: obj.digest,
          versionIndex: obj.version_index,
          path: '',
          scheme: 'weave' as const,
          weaveKind: obj.kind || 'unknown',
          baseObjectClass: obj.base_object_class || null,
          createdAtMs: obj.created_at ? new Date(obj.created_at).getTime() : 0,
          val: obj.val,
          userId: obj.wb_user_id || undefined,
        }));

        versions.sort((a, b) => b.versionIndex - a.versionIndex);

        setVersionCache(prev => new Map(prev).set(objectId, versions));
      } catch (error) {
        console.error('Failed to fetch versions:', error);
      } finally {
        setLoadingVersions(false);
      }
    },
    [versionCache, getTsClient, entity, project]
  );

  useEffect(() => {
    if (hoveredName) {
      fetchVersionsForObject(hoveredName);
    }
  }, [hoveredName, fetchVersionsForObject]);
  useEffect(() => {
    if (selectedName && !versionCache.has(selectedName)) {
      fetchVersionsForObject(selectedName);
    }
  }, [selectedName, versionCache, fetchVersionsForObject]);

  const handleNameClick = useCallback(
    (name: string) => {
      onChange(name);
      setIsOpen(false);
    },
    [onChange]
  );

  const handleVersionClick = useCallback(
    (name: string, hash: string) => {
      onChange(`${name}:${hash}`);
      setIsOpen(false);
    },
    [onChange]
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange(null);
    },
    [onChange]
  );

  useLayoutEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + DROPDOWN_GAP_PX,
        left: rect.left,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const buttonTooltip = selectedName
    ? selectedName
    : `Filter down runs by ${objectClass.toLowerCase()}`;

  return (
    <div ref={containerRef} className="tw-style relative w-[280px]">
      <Tooltip title={buttonTooltip} placement="top" {...TOOLTIP_PROPS}>
        <button
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`
            h-32 w-full flex items-center justify-between px-8
            bg-white rounded-[5px] text-sm text-moon-800
            ${isOpen ? 'border-2 border-teal-400' : 'border border-moon-250'}
            ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-2 hover:border-teal-400'}
          `}
          style={{outline: 'none'}}>
          <span className="flex items-center gap-6 flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
            {icon && <span className="flex-shrink-0">{icon}</span>}
            {selectedName && selectedHash ? (
              <>
                <span className="overflow-hidden text-ellipsis">
                  {selectedName}
                </span>
                <span className="flex-shrink-0">
                  (v
                  {versionCache
                    .get(selectedName)
                    ?.find(v => v.versionHash === selectedHash)?.versionIndex ??
                    '?'}
                  )
                </span>
              </>
            ) : (
              <span className="overflow-hidden text-ellipsis">
                {selectedName || placeholder}
              </span>
            )}
          </span>
          <div className="flex items-center gap-4">
            {value && !disabled && (
              <button
                onClick={handleClear}
                className="flex items-center justify-center p-0 border-none bg-transparent cursor-pointer text-moon-500 hover:text-moon-800">
                <Icon name="close" width={16} height={16} />
              </button>
            )}
            <Icon
              name={isOpen ? 'chevron-up' : 'chevron-down'}
              width={16}
              height={16}
            />
          </div>
        </button>
      </Tooltip>

      {isOpen &&
        dropdownPosition &&
        createPortal(
          <Tailwind>
            <div
              ref={dropdownRef}
              className="fixed bg-white border border-moon-250 rounded flex w-[500px] h-[320px]"
              style={{
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                zIndex: 2000,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              }}
              onMouseLeave={() => handleNameHover(null)}>
              <div
                className="scrollbar-hover overflow-y-auto py-6 flex-[1_1_70%] border-r border-moon-250"
                style={{direction: 'rtl'}}>
                <div style={{direction: 'ltr'}}>
                  {names.length === 0 ? (
                    <div className="p-12 text-moon-500 text-sm text-center">
                      No {objectClass.toLowerCase()}s found
                    </div>
                  ) : (
                    names.map(name => (
                      <Tooltip
                        key={name}
                        title={
                          'Click to view runs using any version of this ' +
                          objectClass.toLowerCase()
                        }
                        placement="left"
                        {...TOOLTIP_PROPS}>
                        <div
                          onMouseEnter={() => handleNameHover(name)}
                          onClick={() => handleNameClick(name)}
                          className={`
                          py-6 px-10 mx-6 rounded cursor-pointer text-sm
                          ${selectedName === name ? 'text-teal-600 bg-teal-300/35 font-semibold' : 'text-moon-800 font-normal'}
                          ${selectedName !== name && hoveredName === name ? 'bg-moon-100' : ''}
                        `}>
                          {name}
                        </div>
                      </Tooltip>
                    ))
                  )}
                </div>
              </div>

              <div className="scrollbar-hover overflow-y-auto py-6 flex-[1_1_30%]">
                {!hoveredName ? (
                  <div className="p-12 text-moon-500 text-sm text-center">
                    Hover over a name to see versions
                  </div>
                ) : loadingVersions && hoveredVersions.length === 0 ? (
                  <div className="p-12 text-moon-500 text-sm text-center flex items-center justify-center gap-8">
                    <Icon name="loading" width={16} height={16} />
                    Loading versions...
                  </div>
                ) : hoveredVersions.length === 0 ? (
                  <div className="p-12 text-moon-500 text-sm text-center">
                    No versions found
                  </div>
                ) : (
                  <>
                    <Tooltip
                      key="all-versions"
                      title={
                        'Click to view runs using any version of this ' +
                        objectClass.toLowerCase()
                      }
                      placement="right"
                      {...TOOLTIP_PROPS}>
                      <div
                        onClick={() =>
                          displayedName && handleNameClick(displayedName)
                        }
                        className={`
                        py-6 px-10 mx-6 rounded cursor-pointer text-sm
                        ${selectedName === displayedName && !selectedHash ? 'text-teal-600 bg-teal-300/35 font-semibold' : 'text-moon-800 font-normal hover:bg-moon-100'}
                      `}>
                        All versions
                      </div>
                    </Tooltip>
                    {hoveredVersions.map(version => {
                      const isSelected =
                        selectedName === displayedName &&
                        selectedHash === version.versionHash;
                      return (
                        <Tooltip
                          key={version.versionHash}
                          title={
                            'Click to view runs using this ' +
                            objectClass.toLowerCase() +
                            ' version'
                          }
                          placement="right"
                          {...TOOLTIP_PROPS}>
                          <div
                            onClick={() =>
                              displayedName &&
                              handleVersionClick(
                                displayedName,
                                version.versionHash
                              )
                            }
                            className={`
                            py-6 px-10 mx-6 rounded cursor-pointer text-sm flex flex-col gap-2
                            ${isSelected ? 'text-teal-600 bg-teal-300/35 font-semibold' : 'text-moon-800 font-normal hover:bg-moon-100'}
                          `}>
                            <div>v{version.versionIndex}</div>
                            <div className="text-xs text-moon-500 font-normal">
                              {formatVersionTimestamp(version.createdAtMs)}
                            </div>
                          </div>
                        </Tooltip>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          </Tailwind>,
          document.body
        )}
    </div>
  );
};
